// Single source of truth for languages and routes.
// This module is also loaded by scripts/prerender.js at build time, so it must
// stay free of imports.

export const LANGUAGES = ['de', 'en'] as const;

export type Language = (typeof LANGUAGES)[number];

/**
 * The default language lives on the bare URLs (/, /about). Every other
 * language is served under its own prefix (/en, /en/about). That keeps the
 * already indexed URLs in place and makes every page self-canonical.
 */
export const DEFAULT_LANGUAGE: Language = 'de';

export const SITE_URL = 'https://me-support.net';

export interface RouteDefinition {
  /** Path without language prefix, e.g. '/about'. */
  path: string;
  /** Key into SEO_CONTENT. */
  seoPage: string;
  /** Component name, resolved to an element in App.tsx. */
  component: string;
}

export const ROUTES: RouteDefinition[] = [
  { path: '/', seoPage: 'home', component: 'HomePage' },
  { path: '/about', seoPage: 'about', component: 'AboutUsPage' },
  { path: '/what-is-me', seoPage: 'whatIsME', component: 'WhatIsMEPage' },
  { path: '/mitmachen', seoPage: 'mitmachen', component: 'MitmachenPage' },
  { path: '/donate', seoPage: 'donate', component: 'DonationPage' },
  { path: '/impressum', seoPage: 'impressum', component: 'ImpressumPage' },
  { path: '/privacy', seoPage: 'privacy', component: 'PrivacyPage' },
  { path: '/cookies', seoPage: 'cookies', component: 'CookiePage' },
  { path: '/cookie-settings', seoPage: 'cookies', component: 'CookieSettingsPage' },
  { path: '/account-deletion', seoPage: 'accountDeletion', component: 'AccountDeletionPage' },
  { path: '/terms', seoPage: 'terms', component: 'TermsPage' },
];

export const isLanguage = (value: string): value is Language =>
  (LANGUAGES as readonly string[]).includes(value);

export const isKnownRoute = (path: string): boolean =>
  ROUTES.some((route) => route.path === path);

/**
 * The URL a route has in a given language.
 * '/about' + 'de' -> '/about'     (default language, no prefix)
 * '/about' + 'en' -> '/en/about'
 * '/'      + 'en' -> '/en'
 */
export const localizedPath = (path: string, language: Language): string => {
  const normalized = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;

  if (language === DEFAULT_LANGUAGE) {
    return normalized || '/';
  }

  return `/${language}${normalized}`;
};

/**
 * Splits a pathname into its language prefix and the bare route path. Paths
 * without a prefix report no language - they are the default language.
 * '/en/about' -> { language: 'en', path: '/about' }
 * '/about'    -> { language: null, path: '/about' }
 */
export const stripLanguage = (pathname: string): { language: Language | null; path: string } => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length > 0 && isLanguage(segments[0])) {
    return {
      language: segments[0],
      path: `/${segments.slice(1).join('/')}`.replace(/\/$/, '') || '/',
    };
  }

  return { language: null, path: pathname.replace(/\/$/, '') || '/' };
};
